import React from 'react';
import { BedDouble, DoorOpen, Car, Moon, Lock, LockOpen, Pencil, ArrowRight, MapPin, Trash2, ArrowUpRight } from 'lucide-react';
import TripLink from '../common/TripLink';
import { PassengerNameList } from './PassengerNameList';
import SourceDocumentLinks from './SourceDocumentLinks';
import StickyNote from './StickyNote';
import { ClockTime } from './TimelineCardVisuals';
import { displayStatus } from '../../utils/componentStatus';

// Trip timeline "Cards" view (modern theme): a hotel's check-in renders as one
// stay card carrying both ends of the stay, and its check-out renders as a card
// built around booking the departure transfer.

const MONO = { fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace" };

// Hotel times are wall-clock values at the hotel; read them as written.
const wallClock = (raw) => {
  const m = String(raw || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  return m ? { date: `${m[1]}-${m[2]}-${m[3]}`, time: m[4] ? `${m[4]}:${m[5]}` : null } : null;
};

// "HH:mm" in the user's time format (12-hour unless they chose 24H)
const formatClock = (time, prefs = {}) => {
  if (!time || prefs.time_view_format === '24H') return time;
  const [hour, minute] = time.split(':').map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const formatDay = (date, withYear = false) => (date
  ? new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', ...(withYear && { year: 'numeric' }) })
    .format(new Date(`${date}T12:00:00Z`))
  : 'Date TBD');

export const hotelStay = (cd = {}) => {
  const checkIn = wallClock(cd.check_in);
  const checkOut = wallClock(cd.check_out);
  const nights = checkIn && checkOut
    ? Math.round((Date.parse(`${checkOut.date}T00:00:00Z`) - Date.parse(`${checkIn.date}T00:00:00Z`)) / 86400000)
    : null;
  return { checkIn, checkOut, nights: nights > 0 ? nights : null };
};

const nightsLabel = (nights) => (nights ? `${nights} night${nights === 1 ? '' : 's'}` : null);

/**
 * The Urbanride booking that takes the guest to the hotel on check-in day
 * ('arrival') or away from it on check-out day ('departure'), if one exists.
 */
export const findHotelTransfer = (hotelData = {}, direction) => {
  const { checkIn, checkOut } = hotelStay(hotelData);
  const day = direction === 'arrival' ? checkIn?.date : checkOut?.date;
  if (!day) return null;
  return (hotelData.urbanride_bookings || []).find((booking) => {
    const end = direction === 'arrival' ? booking.dropoff_type : booking.pickup_type;
    return end === 'hotel' && wallClock(booking.pickup_datetime)?.date === day;
  }) || null;
};

// Component types an Urbanride booking can pick up from or drop off at
export const URBANRIDE_ELIGIBLE_TYPES = [
  'commercial_flight',
  'private_flight',
  'hotel',
  'restaurant',
  'meeting',
  'speaker_meeting',
  'event'
];

/**
 * The two ends of a hotel transfer in a sorted timeline: the item before
 * check-in and the hotel, or the hotel and the item after check-out. `isStop`
 * says which items a ride can start or end at; the hotel's own other event is
 * never one. A missing end is null.
 */
export const hotelTransferEnds = (items, index, isStop) => {
  const hotel = items[index];
  const stop = c => c.originalId !== hotel.originalId && isStop(c);
  return hotel.eventType === 'checkout'
    ? { from: hotel, to: items.slice(index + 1).find(stop) || null }
    : { from: items.slice(0, index).reverse().find(stop) || null, to: hotel };
};

// How a neighbouring timeline item reads as a transfer's other end.
export const describeStop = (component) => {
  if (!component) return null;
  const when = wallClock(component.start_datetime);
  const location = component.location && component.location !== 'Location TBD' ? component.location : null;
  const verb = component.isFlightLeg ? 'Departs' : component.isHotelEvent ? null : 'Starts';
  const time = when?.time && verb ? `${verb} ${formatDay(when.date)} ${when.time}` : null;
  return { title: component.title || 'Next stop', detail: [time, location].filter(Boolean).join(' · ') || null };
};

const STATUS = {
  confirmed: { label: 'Confirmed', className: 'bg-ui-green-bg text-ui-green-fg', dot: 'bg-ui-green-fg' },
  pending: { label: 'Pending confirmation', className: 'bg-ui-amber-bg text-ui-amber-fg', dot: 'bg-ui-amber-fg' },
  cancelled: { label: 'Cancelled', className: 'bg-ui-red-bg text-ui-red-fg', dot: 'bg-ui-red-fg' },
};

const StatusPill = ({ status }) => {
  if (!status) return null;
  const key = status;
  const s = STATUS[key] || { label: key.replace(/_/g, ' '), className: 'bg-ui-hover text-ui-ink-3', dot: 'bg-ui-ink-3' };
  return (
    <span className={`inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-xs font-semibold ${s.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}
    </span>
  );
};

export const hotelGuestNames = (cd = {}, trip) => {
  const execs = Array.isArray(trip?.executives) ? trip.executives : [];
  const execById = new Map(execs.map(e => [String(e.id), `${e.first_name} ${e.last_name}`]));
  const execIds = new Set(execs.map(e => String(e.id)));
  const rooms = Array.isArray(cd.room_details) ? cd.room_details : [];
  const roomGuests = rooms.flatMap(room => (room.guest_ids || []).map((id, i) => ({
    name: room.guest_names?.[i] || execById.get(String(id)) || 'Guest',
    emphasis: execIds.has(String(id)),
  })));
  if (roomGuests.length) return roomGuests;
  const assigned = Array.isArray(cd.assigned_executives) ? cd.assigned_executives : [];
  let names = Array.isArray(cd.executive_names) ? cd.executive_names : [];
  if (!names.length) names = assigned.map(id => execById.get(String(id))).filter(Boolean);
  if (!names.length && assigned.length) names = [`${assigned.length} guest(s)`];
  if (!names.length) names = [cd.primary_guest_speaker_name || cd.primary_guest_name].filter(Boolean);
  return names;
};

const IconTile = ({ Icon }) => (
  <div className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-none bg-ui-teal-bg text-ui-teal-fg">
    <Icon className="w-5 h-5" aria-hidden="true" />
  </div>
);

const ButtonLink = ({ onClick, children, primary = false, label }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    aria-label={label}
    className={primary
      ? 'h-10 px-4 inline-flex items-center gap-1.5 rounded-lg bg-ui-primary hover:bg-ui-primary-hover text-sm font-semibold text-ui-on-primary whitespace-nowrap'
      : 'h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-ui-control text-[13px] text-ui-ink-2 hover:bg-ui-hover whitespace-nowrap'}
  >
    {children}
  </button>
);

const CardFooter = ({ component, confNumber, isConfidential, onOpenConfidentiality, onEdit, onDelete, canViewTrip, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-2.5 px-4 sm:px-[22px] py-3 bg-ui-sunk border-t border-ui-divider">
    <div className="flex items-center gap-1 min-w-0">
      {canViewTrip && component.tripId && (
        <TripLink
          to={`/Trip/${component.tripId}`}
          onClick={e => e.stopPropagation()}
          className="inline-flex items-center gap-1 min-w-0 text-[13px] text-ui-link hover:text-ui-blue-fg"
          aria-label={`View trip timeline: ${component.tripTitle || 'Trip'}`}
        >
          <span className="truncate">{component.tripTitle || 'Trip timeline'}</span>
          <ArrowUpRight className="w-3.5 h-3.5 flex-none" aria-hidden="true" />
        </TripLink>
      )}
      <SourceDocumentLinks component={component} />
      {onOpenConfidentiality && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenConfidentiality(); }}
          aria-label={isConfidential ? 'Confidential: change visibility' : 'Mark confidential'}
          title={isConfidential ? 'Confidential' : 'Mark confidential'}
          className={`w-9 h-9 inline-flex items-center justify-center rounded-lg ${isConfidential ? 'bg-ui-amber-bg text-ui-amber-fg' : 'text-ui-muted hover:text-ui-ink hover:bg-ui-hover'}`}
        >
          {isConfidential ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
        </button>
      )}
      {children}
      {confNumber && <span className="text-[13px] text-ui-ink-3 truncate">Conf. <span className="text-ui-ink" style={MONO}>{confNumber}</span></span>}
    </div>
    <div className="flex items-center gap-2">
      {onEdit && <ButtonLink onClick={onEdit} label="Edit hotel"><Pencil className="w-3.5 h-3.5" />Edit</ButtonLink>}
      {onDelete && <ButtonLink onClick={() => onDelete(component.originalComponent || component)} label="Delete hotel"><Trash2 className="w-3.5 h-3.5" />Delete</ButtonLink>}
    </div>
  </div>
);

const InsideNotes = ({ notes = [], noteHandlers = {}, currentUserId }) => (notes.length > 0 ? (
  <div className="flex flex-col gap-2 px-4 sm:px-[22px] pb-4">
    {notes.map(note => (
      <StickyNote key={note.id} note={note} currentUserId={currentUserId} onToggleExpand={noteHandlers.onToggleNoteExpand} onEdit={noteHandlers.onEditNote} onDelete={noteHandlers.onDeleteNote} />
    ))}
  </div>
) : null);

const TransferBooked = ({ booking, title }) => (
  <div className="flex items-center gap-3 px-3.5 py-3 rounded-[10px] bg-ui-green-bg border border-ui-green-line">
    <Car className="w-4 h-4 flex-none text-ui-green-fg" aria-hidden="true" />
    <div className="min-w-0">
      <div className="text-sm font-semibold text-ui-ink">
        {title}{wallClock(booking.pickup_datetime)?.time && <> · <span style={MONO}>{wallClock(booking.pickup_datetime).time}</span></>}
      </div>
      <div className="text-[13px] text-ui-ink-3 truncate">
        {[`${booking.pickup_location || 'Pickup'} → ${booking.dropoff_location || 'Drop-off'}`, booking.vehicle_class, booking.urcn && `Ride ${booking.urcn}`].filter(Boolean).join(' · ')}
      </div>
    </div>
  </div>
);

export const HotelStayCard = ({
  component, trip, arrivalTransfer, arrivalFrom, onBookArrival, onEdit, onDelete, canViewTrip = false, isConfidential, onOpenConfidentiality,
  insideNotes, noteHandlers, currentUserId, prefs,
}) => {
  const cd = component.hotelData || component.component_data || {};
  const name = cd.hotel_name || component.title?.replace(/^Check-in:\s*/, '') || 'Hotel';
  const { checkIn, checkOut, nights } = hotelStay(cd);
  const address = component.location && component.location !== 'Location TBD' ? component.location : null;
  const guests = hotelGuestNames(cd, trip);
  const confNumber = cd.confirmation_number || component.confirmation_number;

  return (
    <article className="bg-ui-surface border border-ui-line rounded-2xl overflow-hidden shadow-ui-card" aria-label={`Hotel stay: ${name}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-[22px] py-[18px] border-b border-ui-divider">
        <div className="flex items-center gap-3.5 min-w-0">
          <IconTile Icon={BedDouble} />
          <div className="min-w-0">
            <h3 className="m-0 text-lg font-bold tracking-tight text-ui-ink break-words">{name}</h3>
            {address && <div className="text-[13px] text-ui-muted mt-0.5 truncate">{address}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={displayStatus(component)} />
          {nightsLabel(nights) && <span className="text-[13px] font-semibold text-ui-ink-2">{nightsLabel(nights)}</span>}
        </div>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:gap-4 px-4 sm:px-[22px] py-5">
        <div>
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ui-muted">Check-in</div>
          <div className="text-[22px] sm:text-[26px] font-semibold text-ui-ink mt-1.5" style={MONO}><ClockTime value={formatClock(checkIn?.time, prefs)} /></div>
          <div className="text-[13px] text-ui-ink-2 mt-0.5">{formatDay(checkIn?.date, true)}</div>
        </div>
        <div className="flex flex-col items-center gap-1.5 w-16 sm:w-[140px]">
          <div className="flex items-center w-full gap-1.5" aria-hidden="true">
            <span className="w-2.5 h-2.5 rounded-full bg-ui-teal-fg flex-none" />
            <span className="flex-1 h-0.5 bg-ui-teal-line" />
            <Moon className="w-3.5 h-3.5 text-ui-teal-fg flex-none" />
            <span className="flex-1 h-0.5 bg-ui-teal-line" />
            <span className="w-2.5 h-2.5 rounded-full border-2 border-ui-teal-fg flex-none" />
          </div>
          {nightsLabel(nights) && <span className="text-xs text-ui-muted">{nightsLabel(nights)}</span>}
        </div>
        <div className="text-right">
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ui-muted">Check-out</div>
          <div className="text-[22px] sm:text-[26px] font-semibold text-ui-ink mt-1.5" style={MONO}><ClockTime value={formatClock(checkOut?.time, prefs)} /></div>
          <div className="text-[13px] text-ui-ink-2 mt-0.5">{formatDay(checkOut?.date, true)}</div>
        </div>
      </div>

      {(arrivalTransfer || onBookArrival) && (
        <div className="px-4 sm:px-[22px] pb-[18px]">
          {arrivalTransfer ? <TransferBooked booking={arrivalTransfer} title="Arrival transfer booked" /> : (
            <div className="flex flex-wrap items-center justify-between gap-3 px-3.5 py-2.5 rounded-[10px] bg-ui-blue-bg border border-ui-blue-line">
              <span className="flex items-center gap-2.5 text-[13px] text-ui-blue-fg min-w-0">
                <Car className="w-4 h-4 flex-none" aria-hidden="true" />
                <span>No ride to the hotel yet{arrivalFrom ? `, from ${arrivalFrom}` : ''}.</span>
              </span>
              <ButtonLink onClick={onBookArrival}>Book ride</ButtonLink>
            </div>
          )}
        </div>
      )}

      {(address || guests.length > 0) && (
        <dl className="m-0 grid grid-cols-1 sm:grid-cols-2 gap-4 px-4 sm:px-[22px] py-4 border-t border-ui-divider">
          {address && (
            <div className="min-w-0">
              <dt className="text-xs text-ui-muted">Address</dt>
              <dd className="m-0 mt-1 text-[13px] text-ui-ink break-words">{address}</dd>
              <dd className="m-0 mt-1">
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${address}`)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-ui-link hover:text-ui-blue-fg">
                  <MapPin className="w-3 h-3" aria-hidden="true" />Directions
                </a>
              </dd>
            </div>
          )}
          {guests.length > 0 && (
            <div className="min-w-0 [&_.text-slate-500]:text-ui-muted">
              <dt className="text-xs text-ui-muted">{guests.length === 1 ? 'Guest' : 'Guests'}</dt>
              <dd className="m-0 mt-1"><PassengerNameList names={guests} variant="modern" /></dd>
            </div>
          )}
        </dl>
      )}

      <InsideNotes notes={insideNotes} noteHandlers={noteHandlers} currentUserId={currentUserId} />
      <CardFooter component={component} confNumber={confNumber} isConfidential={isConfidential} onOpenConfidentiality={onOpenConfidentiality} onEdit={onEdit} onDelete={onDelete} canViewTrip={canViewTrip} />
    </article>
  );
};

export const HotelCheckoutCard = ({
  component, departureTransfer, nextStop, onBookDeparture, onEdit, onDelete, canViewTrip = false, isConfidential, onOpenConfidentiality,
  insideNotes, noteHandlers, currentUserId, prefs,
}) => {
  const cd = component.hotelData || component.component_data || {};
  const name = cd.hotel_name || component.title?.replace(/^Check-out:\s*/, '') || 'Hotel';
  const { checkIn, checkOut, nights } = hotelStay(cd);
  const address = component.location && component.location !== 'Location TBD' ? component.location : null;
  const confNumber = cd.confirmation_number || component.confirmation_number;
  const stayLine = [nights ? `End of a ${nights}-night stay` : 'End of stay', checkIn?.date && `from ${formatDay(checkIn.date)}`].filter(Boolean).join(' ');

  return (
    <article className="bg-ui-surface border border-ui-line rounded-2xl overflow-hidden shadow-ui-card" aria-label={`Check-out: ${name}`}>
      <div className="flex items-center justify-between gap-3 px-4 sm:px-[22px] py-[18px] border-b border-ui-divider">
        <div className="flex items-center gap-3.5 min-w-0">
          <IconTile Icon={DoorOpen} />
          <div className="min-w-0">
            <h3 className="m-0 text-lg font-bold tracking-tight text-ui-ink break-words">Check out of {name}</h3>
            {stayLine && <div className="text-[13px] text-ui-muted mt-0.5">{stayLine}</div>}
          </div>
        </div>
        <div className="text-right flex-none">
          <div className="text-[22px] sm:text-[26px] font-semibold text-ui-ink" style={MONO}><ClockTime value={formatClock(checkOut?.time, prefs)} /></div>
          <div className="text-xs text-ui-muted">Check-out by</div>
        </div>
      </div>

      {(departureTransfer || onBookDeparture) && (
        <div className="px-4 sm:px-[22px] py-[18px]">
          {departureTransfer ? <TransferBooked booking={departureTransfer} title="Departure transfer booked" /> : (
            <section className="rounded-xl bg-ui-blue-bg border border-ui-blue-line overflow-hidden" aria-label="Departure transfer">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 border-b border-ui-blue-line">
                <span className="flex items-center gap-2.5 text-[15px] font-semibold text-ui-ink">
                  <Car className="w-[18px] h-[18px] text-ui-link" aria-hidden="true" />Departure transfer
                </span>
                <span className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-xs font-semibold bg-ui-amber-bg text-ui-amber-fg">
                  <span className="w-1.5 h-1.5 rounded-full bg-ui-amber-fg" />Not booked
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] items-center gap-3 px-4 py-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ui-muted">Pickup</div>
                  <div className="text-sm font-semibold text-ui-ink mt-1 break-words">{name}</div>
                  {address && <div className="text-[13px] text-ui-ink-3 break-words">{address}</div>}
                  {checkOut?.time && <div className="text-sm text-ui-blue-fg mt-1.5" style={MONO}>{formatClock(checkOut.time, prefs)} · at check-out</div>}
                </div>
                <ArrowRight className="hidden sm:block w-5 h-5 text-ui-blue-solid" aria-hidden="true" />
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ui-muted">Drop-off</div>
                  {nextStop ? (
                    <>
                      <div className="text-sm font-semibold text-ui-ink mt-1 break-words">{nextStop.title}</div>
                      {nextStop.detail && <div className="text-[13px] text-ui-ink-3 break-words">{nextStop.detail}</div>}
                    </>
                  ) : (
                    <div className="text-[13px] text-ui-ink-3 mt-1">Choose where the ride goes in the next step</div>
                  )}
                </div>
              </div>
              <div className="flex justify-end px-4 py-3 bg-ui-blue-bg border-t border-ui-blue-line">
                <ButtonLink primary onClick={onBookDeparture}><Car className="w-4 h-4" aria-hidden="true" />Book departure transfer</ButtonLink>
              </div>
            </section>
          )}
        </div>
      )}

      <InsideNotes notes={insideNotes} noteHandlers={noteHandlers} currentUserId={currentUserId} />
      <CardFooter component={component} confNumber={confNumber} isConfidential={isConfidential} onOpenConfidentiality={onOpenConfidentiality} onEdit={onEdit} onDelete={onDelete} canViewTrip={canViewTrip} />
    </article>
  );
};
